import { useRouteLocationWithLocale, useRouter } from '#imports'

import type { Orderline } from '@backmarket/http-api/src/api-specs-my-orders-api/types/orderlineListResponse'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { openModal } from '@ds/components/ModalBase'

import { CUSTOMER_CARE as ROUTES_CUSTOMER_CARE } from '@/scopes/customer-care/routes'
import { CUSTOMER_REQUESTS as ROUTES_CUSTOMER_REQUESTS } from '@/scopes/customer-requests/routes'
import { DASHBOARD as ROUTES_DASHBOARD } from '@/scopes/dashboard/routes'
import { REPAIR as ROUTES_REPAIR } from '@/scopes/repair/routes'
import { ROUTES as ROUTES_REVIEWS } from '@/scopes/reviews/route-names'

import { ORDERLINE_CARD_MODAL } from '../OrderlineCard.constants'

export type OrderlineCardActionsConfig = {
  config: {
    [key in Orderline['actions'][number]['type']]: (
      item: Orderline['actions'][number],
    ) => {
      onClick?: ({
        orderlineId,
      }: {
        orderlineId: string | number
      }) => Promise<void>
    }
  }
}

export const useOrderlineCardActionsConfig = (): OrderlineCardActionsConfig => {
  const injectLocale = useRouteLocationWithLocale()
  const router = useRouter()
  const tracking = useTracking()

  const defineRouterLink = ({
    name,
    params,
  }: {
    name: string
    params: Record<string, string | number>
  }) => {
    return toNuxtLink(
      injectLocale({
        type: 'internal',
        name,
        params,
      }),
    )
  }

  return {
    config: {
      cancel_order_before_validation: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { orderId, orderlineId } = item.link.params

            await router.push(
              defineRouterLink({
                name: ROUTES_DASHBOARD.MY_ORDERS.CANCEL,
                params: { orderId, orderlineId },
              }),
            )
          }
        },
      }),
      cancel_order_pending_payment: () => ({
        onClick: async ({ orderlineId }) => {
          openModal(
            `${ORDERLINE_CARD_MODAL.cancelOrderWithPendingPayment}-${orderlineId}`,
          )
        },
      }),
      diagnosis: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { orderlineId } = item.link.params

            await router.push(
              defineRouterLink({
                name: ROUTES_CUSTOMER_CARE.DIAGNOSIS.SELF,
                params: { orderlineId },
              }),
            )
          }
        },
      }),
      diagnosis_new_issue: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { orderlineId } = item.link.params

            await router.push(
              defineRouterLink({
                name: ROUTES_CUSTOMER_CARE.DIAGNOSIS.SELF,
                params: { orderlineId },
              }),
            )
          }
        },
      }),
      get_more_help: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            await router.push(
              defineRouterLink({
                name: ROUTES_CUSTOMER_REQUESTS.ASP_RESOLUTION_CONVERSATION,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      join_conversation: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            await router.push(
              defineRouterLink({
                name: ROUTES_CUSTOMER_REQUESTS.ASP_RESOLUTION_CONVERSATION,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      live_chat: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            await router.push(
              defineRouterLink({
                name: ROUTES_CUSTOMER_REQUESTS.ASP_RESOLUTION_CONVERSATION,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      order_bill: (item) => ({
        onClick: async () => {
          if (!isEmpty(item.link)) {
            window.open(item.link.href, '_blank', 'noopener,noreferrer')
          }
        },
      }),
      pending_order_bill: () => ({}),
      rate_order: () => ({
        onClick: async ({ orderlineId }) => {
          tracking.trackClick({
            name: 'leave_review',
            zone: 'my_oders',
          })

          // Due to issues encountered in SCAN-6093 we use the
          // internally resolved link instead of the one from API.
          const routeLocation = defineRouterLink({
            name: ROUTES_REVIEWS.FORM,
            params: { id: orderlineId },
          })
          const { href } = router.resolve(routeLocation)

          window.open(href, '_blank', 'noopener,noreferrer')
        },
      }),
      sale_certificate: (item) => ({
        onClick: async () => {
          if (!isEmpty(item.link)) {
            window.open(item.link.href, '_blank', 'noopener,noreferrer')
          }
        },
      }),
      see_return_confirmation: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            await router.push(
              defineRouterLink({
                name: ROUTES_CUSTOMER_CARE.RESOLUTION
                  .RESOLUTION_RETURN_CONFIRMATION,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      track_parcel: (item) => ({
        onClick: async () => {
          if (!isEmpty(item.link)) {
            window.open(item.link.href, '_blank', 'noopener,noreferrer')
          }
        },
      }),
      track_repair_picked_up: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            await router.push(
              defineRouterLink({
                name: ROUTES_CUSTOMER_REQUESTS.ASP_RESOLUTION_CONVERSATION,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      track_repair_not_picked_up: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { resolutionId } = item.link.params

            await router.push(
              defineRouterLink({
                name: ROUTES_CUSTOMER_CARE.RESOLUTION
                  .RESOLUTION_RETURN_CONFIRMATION,
                params: { resolutionId },
              }),
            )
          }
        },
      }),
      track_shop_repair: (item) => ({
        onClick: async () => {
          if (item?.link && 'params' in item.link) {
            const { repairId } = item.link.params

            await router.push(
              defineRouterLink({
                name: ROUTES_REPAIR.TRACKING,
                params: { repairId },
              }),
            )
          }
        },
      }),
    },
  }
}
